import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, TextField,
  FormControl, InputLabel, Select, MenuItem, Button, Collapse, useTheme, useMediaQuery
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import CardGiftcardIcon from '@mui/icons-material/EventAvailable';
import ToggleCard from '../components/ToggleCard';

const HotelBooking: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);

  // Dates
  const today = new Date();
  const opt: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
  const checkIn = today.toLocaleDateString('en-US', opt);
  const out = new Date(today);
  out.setDate(today.getDate() + 3);
  const checkOut = out.toLocaleDateString('en-US', opt);

  // Impact data
  const data = {
    impact: { name: 'Mangroves', verb: 'Restored', amount: 3, description: 'In Bali\'s coastal regions, protecting communities and wildlife' },
    coastalProtection: 'Strong mangrove roots prevent coastal erosion and protect communities from storm surges.',
    marineLife: 'Creates habitats for fish, crustaceans, and other marine species.',
    carbonImpactDescription: 'CO₂ equivalent absorbed by these mangroves.',
    carbonImpactValue: '37 kg',
  };

  const handleBooking = () => setBookingConfirmed(true);

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 2 }}>
        Hotel Checkout
      </Typography>
      <Typography variant="subtitle1" color="text.primary" sx={{ mb: 4 }}>
        Complete your booking while supporting regenerative projects worldwide with our API
      </Typography>

      <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} gap={4}>
        {/* Booking Form */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                <HotelIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Confirm Your Stay
              </Typography>

              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField fullWidth label="Hotel" value="Bamboo Eco Resort & Spa, Bali" disabled />
                <Box sx={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: 2 }}>
                  <TextField fullWidth label="Check In" value={checkIn} disabled />
                  <TextField fullWidth label="Check Out" value={checkOut} disabled />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: 2 }}>
                  <FormControl fullWidth disabled>
                    <InputLabel>Nights</InputLabel>
                    <Select label="Nights" value="3 nights">
                      <MenuItem value="3 nights">3 nights</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth disabled>
                    <InputLabel>Guests</InputLabel>
                    <Select label="Guests" value="2 guests">
                      <MenuItem value="2 guests">2 guests</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Card sx={{ backgroundColor: 'rgb(239,253,219)' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Your booking includes regenerative impact
                    </Typography>
                    <Typography>Every booking restores 1 mangrove per night in Indonesia's coastal regions</Typography>
                  </CardContent>
                </Card>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ py: 1.5, fontWeight: 700 }}
                  disabled={bookingConfirmed}
                  onClick={handleBooking}
                >
                  Book your stay now
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Confirmation Section */}
        <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '64px' }}>
          <Collapse
            in={bookingConfirmed}
            timeout={600}
            unmountOnExit
            onEntered={() => confirmRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Card>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CardGiftcardIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} /> Booking Confirmed
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Your booking is confirmed! See how your stay is making a difference.
                </Typography>

                <ToggleCard title="Mangroves Restored" code={{ impact: data.impact }}>
                  <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', fontWeight: 700 }}>3</Typography>
                  <Typography>In Bali's coastal regions, protecting communities and wildlife</Typography>
                </ToggleCard>

                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Impact Breakdown</Typography>
                <ToggleCard
                  title="Coastal Protection 🌊"
                  code={{ title: "Coastal Protection 🌊", description: data.coastalProtection }}
                >
                  <Typography variant="body2">{data.coastalProtection}</Typography>
                </ToggleCard>
                <ToggleCard
                  title="Marine Life 🐠"
                  code={{ title: "Marine Life 🐠", description: data.marineLife }}
                >
                  <Typography variant="body2">{data.marineLife}</Typography>
                </ToggleCard>
                <ToggleCard
                  title="Carbon Impact 🌱"
                  code={{ title: "Carbon Impact 🌱", description: data.carbonImpactDescription, value: data.carbonImpactValue }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{data.carbonImpactDescription}</Typography>
                    <Typography variant="h6" sx={{ color: 'rgb(79,111,82)', fontWeight: 700 }}>{data.carbonImpactValue}</Typography>
                  </Box>
                </ToggleCard>
                {/* Tracking CTA */}
                <Box
                  component={Link}
                  to="/engagement"
                  sx={{
                    textDecoration: 'none',     // remove underline
                    color: 'inherit',           // inherit text colour
                    backgroundColor: 'rgb(239,253,219)',
                    p: 2,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgb(227,244,204)',
                      textDecoration: 'none'    // ensure no underline on hover
                    }
                  }}
                >
                  <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                  <Typography>{'→'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      </Box>
    </Container>
  );
};

export default HotelBooking;
